window.CartAddUtils = (function () {
  const SUBSCRIPTION_PRODUCT_TYPES = ['meals', 'bundle', 'drinks', 'snacks'];

  function isSubscriptionProductType(productType) {
    return SUBSCRIPTION_PRODUCT_TYPES.includes((productType || '').toLowerCase());
  }

  function getFrequencyConstants() {
    const strings = window.deliveryFrequencyStrings || {};
    return {
      oneWeek: strings.oneWeek || 'Delivery every 1 Week',
      twoWeeks: strings.twoWeeks || 'Delivery every 2 Weeks',
    };
  }

  function normalizeId(value) {
    if (value == null || value === '') return null;
    return `${value}`;
  }

  function isOneTimeFrequency(frequencyValue) {
    return frequencyValue === '' || frequencyValue == null;
  }

  function getModalSelectedFrequency() {
    const checked = document.querySelector(
      'delivery-frequency-modal input[name="df_purchase_option"]:checked'
    );
    if (!checked) return null;

    return {
      value: checked.value,
      isOneTime: isOneTimeFrequency(checked.value),
      label: checked.getAttribute('data-label') || checked.value || 'One-off',
    };
  }

  async function fetchCart() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return null;
      return response.json();
    } catch (error) {
      return null;
    }
  }

  function mapPlanIdToFrequency(planId, lineItemData, constants) {
    const normalizedPlanId = normalizeId(planId);
    if (!normalizedPlanId) {
      return { value: '', isOneTime: true };
    }

    const oneWeekId = normalizeId(lineItemData?.selling_plan_1_week_id);
    const twoWeeksId = normalizeId(lineItemData?.selling_plan_2_weeks_id);

    if (oneWeekId && normalizedPlanId === oneWeekId) {
      return { value: constants.oneWeek, isOneTime: false };
    }
    if (twoWeeksId && normalizedPlanId === twoWeeksId) {
      return { value: constants.twoWeeks, isOneTime: false };
    }

    const plans = lineItemData?.selling_plans || [];
    const planIndex = plans.findIndex((plan) => normalizeId(plan.id) === normalizedPlanId);
    if (planIndex === 0) {
      return { value: constants.oneWeek, isOneTime: false };
    }
    if (planIndex === 1) {
      return { value: constants.twoWeeks, isOneTime: false };
    }

    return { value: constants.oneWeek, isOneTime: false };
  }

  async function resolveActiveFrequency() {
    const modalFrequency = getModalSelectedFrequency();
    if (modalFrequency) return modalFrequency;

    const cart = await fetchCart();
    if (!cart?.items?.length) return null;

    const constants = getFrequencyConstants();
    const subscriptionItem = cart.items.find((item) => item.selling_plan_allocation);
    if (!subscriptionItem) {
      return { value: '', isOneTime: true, label: 'One-off' };
    }

    const planId = subscriptionItem.selling_plan_allocation?.selling_plan?.id;
    return mapPlanIdToFrequency(planId, null, constants);
  }

  async function cartHasSubscription() {
    const cart = await fetchCart();
    if (!cart?.items?.length) return false;
    return cart.items.some((item) => item.selling_plan_allocation);
  }

  function resolveSellingPlanIdForLine({ productType, frequencyValue, lineItemData }) {
    if (!isSubscriptionProductType(productType)) return null;

    const constants = getFrequencyConstants();
    const plans = lineItemData?.selling_plans || [];
    const orderPlanId = normalizeId(lineItemData?.selling_plan_id);
    const oneWeekId = normalizeId(lineItemData?.selling_plan_1_week_id);
    const twoWeeksId = normalizeId(lineItemData?.selling_plan_2_weeks_id);

    if (orderPlanId && plans.some((plan) => normalizeId(plan.id) === orderPlanId)) {
      return orderPlanId;
    }

    const resolvedFrequency =
      frequencyValue !== undefined ? frequencyValue : constants.oneWeek;

    if (isOneTimeFrequency(resolvedFrequency)) {
      return null;
    }

    if (resolvedFrequency === constants.oneWeek) {
      return oneWeekId || normalizeId(plans[0]?.id) || null;
    }

    if (resolvedFrequency === constants.twoWeeks) {
      return twoWeeksId || normalizeId(plans[1]?.id) || normalizeId(plans[0]?.id) || null;
    }

    return oneWeekId || normalizeId(plans[0]?.id) || null;
  }

  function resolveSellingPlanIdFromSelect(sellingPlanSelect, frequencyValue) {
    if (!sellingPlanSelect) return null;

    const productType = sellingPlanSelect.closest('cre-product-form')?.getAttribute('data-product-type');
    if (!isSubscriptionProductType(productType)) return null;

    const plans = Array.from(sellingPlanSelect.querySelectorAll('option')).map((option) => ({
      id: option.value,
      name: option.getAttribute('data-selling-plan-name') || option.textContent.trim(),
    }));

    const oneWeekId = normalizeId(plans[0]?.id);
    const twoWeeksId = normalizeId(plans[1]?.id);

    return resolveSellingPlanIdForLine({
      productType,
      frequencyValue,
      lineItemData: {
        selling_plans: plans,
        selling_plan_1_week_id: oneWeekId,
        selling_plan_2_weeks_id: twoWeeksId,
      },
    });
  }

  async function resolveFrequencyForAddToCart(sellingPlanSelect) {
    const modalFrequency = getModalSelectedFrequency();
    if (modalFrequency) return modalFrequency.value;

    const activeFrequency = await resolveActiveFrequency();
    if (activeFrequency) return activeFrequency.value;

    const legacyRadio = document.querySelector(
      'cart-drawer #selling-plan-options input[name="purchase_option"]:checked'
    );
    if (legacyRadio) return legacyRadio.value;

    return '';
  }

  function appendRechargeBundleProperties(cartItem, productType) {
    if (!isSubscriptionProductType(productType)) return cartItem;

    cartItem.properties = cartItem.properties || {};

    if (typeof rc_bundle_id !== 'undefined') {
      cartItem.properties._rc_bundle = rc_bundle_id;
    }
    if (typeof rc_bundle_parent !== 'undefined') {
      cartItem.properties._rc_bundle_parent = rc_bundle_parent;
    }
    if (typeof rc_bundle_variant !== 'undefined') {
      cartItem.properties._rc_bundle_variant = rc_bundle_variant;
    }
    if (typeof rc_bundle_collection_id !== 'undefined') {
      cartItem.properties._rc_bundle_collection_id = rc_bundle_collection_id;
    }

    return cartItem;
  }

  async function buildCartAddItem(lineItemData, quantityOverride, options = {}) {
    const quantity = quantityOverride ?? lineItemData.quantity ?? 1;
    const productType = (lineItemData.product_type || '').toLowerCase();
    const preferOrderPlan = options.preferOrderPlan !== false;
    const orderPlanId = normalizeId(lineItemData?.selling_plan_id);
    const constants = getFrequencyConstants();

    let frequencyValue = options.frequencyValue;

    if (preferOrderPlan && !orderPlanId) {
      frequencyValue = '';
    } else if (preferOrderPlan && orderPlanId) {
      const planStillExists = (lineItemData.selling_plans || []).some(
        (plan) => normalizeId(plan.id) === orderPlanId
      );
      if (planStillExists) {
        frequencyValue = mapPlanIdToFrequency(orderPlanId, lineItemData, constants).value;
      }
    }

    if (frequencyValue === undefined) {
      const activeFrequency = await resolveActiveFrequency();
      frequencyValue = activeFrequency?.value;
    }

    if (frequencyValue === undefined && isSubscriptionProductType(productType)) {
      frequencyValue = constants.oneWeek;
    }

    if (frequencyValue === undefined) {
      frequencyValue = '';
    }

    const cartItem = {
      id: lineItemData.variant_id,
      quantity,
    };

    if (lineItemData.properties && Object.keys(lineItemData.properties).length) {
      cartItem.properties = { ...lineItemData.properties };
    }

    const sellingPlanId = resolveSellingPlanIdForLine({
      productType,
      frequencyValue,
      lineItemData,
    });

    if (sellingPlanId) {
      cartItem.selling_plan = sellingPlanId;
    }

    appendRechargeBundleProperties(cartItem, productType);

    return cartItem;
  }

  return {
    SUBSCRIPTION_PRODUCT_TYPES,
    isSubscriptionProductType,
    getFrequencyConstants,
    getModalSelectedFrequency,
    resolveActiveFrequency,
    cartHasSubscription,
    resolveSellingPlanIdForLine,
    resolveSellingPlanIdFromSelect,
    resolveFrequencyForAddToCart,
    appendRechargeBundleProperties,
    buildCartAddItem,
  };
})();
